import { showToast } from 'components/ToastNotification';
import { push, go } from 'redux-first-history';
import { matchPath } from 'react-router';
import { Task } from 'redux-saga';
import { all, call, cancel, delay, fork, put, select, take, takeEvery, takeLatest } from 'redux-saga/effects';
import {
  addCartItemLight,
  addFavorite,
  checkAndMergeGuestCartApi,
  fetchRecommendSkuForSigninUser,
  fetchSearchHistory,
  fetchTrendingSearchForGuestUser,
  getBaseUoms as getAllBaseUoms,
  getNetUoms as getAllNetUoms,
  getAllUoms,
  getBrands,
  getBrandsRelatedToCategory,
  getBuyer,
  getCart,
  getCategories,
  getDeliveryFee as getDeliveryFeeQuery,
  getListHolidays,
  getOrderDisabled,
  getOrderStatistics,
  getOrigins,
  getProducts,
  getProductTagList,
  getUser,
  getVaAndQrInfoData,
  handleCheckBadWord as handleCheckBadWordApi,
  handleDeleteSearchHistory,
  handlePingUser,
  handleSaveSearchHistory as handleSaveSearchHistoryApi,
  preloadCategoriesAndRevalidate,
  removeCartItemLight,
  removeFavorite,
  setProductNote,
  signin,
  updateStoreBasicInfoApi,
  getAddStoreDisabled,
} from 'utils/apollo';
import {
  addedToCart as addedToCartAction,
  addingToCart as addingToCartAction,
  brandsByCategory as brandsActions,
  trustedBrands as trustedBrandsActions,
  buyer as buyerActions,
  cart as cartActions,
  categories as categoriesActions,
  changeSelectedStore,
  favorite as favoriteActions,
  favoriteCategories as favoriteCategoriesActions,
  getDeliveryFee as getDeliveryFeeAction,
  orderDisabled,
  productNote as productNoteActions,
  recentOrders as recentOrdersActions,
  refreshCart,
  setBuyerWebVersion,
  setOpenSigninPopup,
  setProductTags,
  setRecommendationProductsForNewCustomer,
  setRecommendSkusForSigninUser,
  setSearchHistory,
  setSearchProductResults,
  setSearchProductResultsLoading,
  setSelectedStore,
  setSpecialDates,
  setTrendingSearchForGuestUser,
  setVaInfo,
  sideNavClose,
  signout as signoutActions,
  signoutSuccess as signoutSuccessActions,
  updateCart,
  updateItemCart as updateItemCartActions,
  updateNotAvailableProducts,
  updateNoteToCard,
  updateProductItemQuantity,
  user as userActions,
  setFooterState,
  openSelectStoreModal,
} from './actions';
import {
  selectBuyer,
  selectSearchHistory,
  selectSearchProductResults,
  selectStore,
  selectUser,
  selectUserPermissions,
} from './selectors';

import { message } from 'antd';
import messages from 'containers/SignupPage/messages';
import moment from 'moment';
import translations from 'translations';
import {
  AccountBuyerStatus,
  ProductSortField,
  QueryProductSearchArgs,
  SortDirection,
  UomElement,
  UpdateCartAction,
} from 'types/schema';
import { KAMEREO_SUPPLIER_ID } from 'utils/constants';
import { hasStaleCache, invalidateCache, readFreshCache, writeCache } from 'utils/localStorageCache';
import { readIdbCacheEntry, writeIdbCache } from 'utils/idbCache';
import utilMessages from 'utils/messages';
import { viewPermissions } from 'utils/permissionMap';
import triggerGA4EcommerceEvent, { EventName, sendGA4Event, triggerGA4SendUserIdEvent } from 'utils/triggerGA4Event';
import calculateVAT from '../../utils/calculateVAT';
import { getPurchasedItemsMightFavorite } from '../HomePage/saga';
import ActionTypes from './constants';
import { markNotificationAsRead, notificationsFlow, registerToken, unregisterToken } from './notifications.saga';

function* recentOrders() {
  const store = yield call(getStoreWithRetry);
  if (!store) {
    return;
  }
  const countStart = moment().startOf('day').toISOString();
  const countEnd = moment().endOf('day').toISOString();
  const aggregateStart = moment().startOf('month').toISOString();
  const aggregateEnd = moment().endOf('month').toISOString();
  yield put(recentOrdersActions.request());
  const response = yield call(getOrderStatistics, {
    storeId: store.id,
    countStart: countStart,
    countEnd: countEnd,
    aggregateStart: aggregateStart,
    aggregateEnd: aggregateEnd,
  });
  if (!response.errors) {
    yield put(
      recentOrdersActions.success({
        countPendingOrders: response.orderCountSummary.pendingApproval,
      }),
    );
  } else {
    yield put(recentOrdersActions.failure(response.errors));
  }
}

function* initCartFlow() {
  const store = yield call(getStore);
  const loggedIn = !!localStorage.getItem('token');

  if (!store && loggedIn) {
    return;
  }
  yield put(orderDisabled.request());
  yield put(cartActions.request());

  const response = yield call(getCart, { storeId: store?.id });

  if (!response.errors) {
    let notAvailableProducts = [];
    let newCart = response;
    if (response?.cartSet && response.cartSet.length > 0) {
      for (let i = 0; i < response.cartSet.length; i += 1) {
        if (response?.cartSet[i]?.supplier) {
          const additionalProducts = response?.cartSet[i].items.filter(
            (item: any) => !item?.product?.allowPurchase || !item?.product?.inStock,
          );
          let newTotal = response?.cartSet[i].total;
          additionalProducts.forEach((item: any) => {
            const priceItemExcludedVAT =
              calculateVAT(
                item?.product?.priceV2?.price,
                item?.product?.priceV2?.vat,
                item?.product?.priceV2?.vatIncluded,
              ).beforeVat * item.quantity;
            const priceItemIncludedVAT =
              priceItemExcludedVAT + Math.round((priceItemExcludedVAT * item?.product?.priceV2?.vat) / 100);
            newTotal -= priceItemIncludedVAT;
          });
          notAvailableProducts = notAvailableProducts.concat(additionalProducts);
          newCart = {
            ...newCart,
            cartSet: [
              ...newCart.cartSet.slice(0, i),
              {
                ...newCart.cartSet[i],
                items: newCart.cartSet[i].items.filter(
                  (item: any) => item?.product?.allowPurchase && item?.product?.inStock,
                ),
                total: newTotal,
              },
              ...newCart.cartSet.slice(i + 1),
            ],
          };
        }
      }
    }
    yield put(updateNotAvailableProducts(notAvailableProducts));
    yield put(cartActions.success(newCart));
  } else {
    yield put(cartActions.failure(response.errors));
  }
}

export const storeSelector = selectStore();

export function* getStore() {
  const store = yield select(storeSelector);
  if (store.loading) {
    return null;
  }
  if (store.id !== localStorage.getItem('store')) {
    localStorage.removeItem('store');
    yield put(openSelectStoreModal(true));
    return null;
  }

  // Sanity check passed
  return store;
}

export function* getStoreWithRetry() {
  let store = yield call(getStore);
  if (!store) {
    yield take(ActionTypes.SET_SELECTED_STORE);
    store = yield call(getStore);
  }
  return store;
}

export function* pingUserPolling() {
  while (true) {
    const user = yield select(selectUser());
    yield call(handlePingUser, {
      input: {
        userId: user.id,
      },
    });
    yield delay(900000);
  }
}

function* initUserFlow() {
  const loggedIn = !!localStorage.getItem('token');

  if (loggedIn) {
    yield put(userActions.request());
    const response = yield call(getUser);
    if (!response.errors) {
      const user = response;
      yield put(userActions.success(user));
      const userPermissions = yield select(selectUserPermissions());
      if (userPermissions?.length) {
        const paths = Object.keys(viewPermissions);
        const pathname = yield select((state: any) => state.router.location.pathname);
        for (const path of paths) {
          const route = matchPath(pathname, { path: path });
          if (route) {
            const permissions = viewPermissions[route.path];
            if (permissions) {
              let permissionCounter = 0;
              for (const permission of userPermissions) {
                if (permissions.indexOf(permission) > -1) {
                  permissionCounter += 1;
                }
              }
              if (permissionCounter !== permissions.length) {
                yield put(push('/'));
              }
            }
          }
        }
      }
      triggerGA4SendUserIdEvent(user.id);
      if (window?.clarity) {
        window.clarity('set', 'user_email', user.email);
        window.clarity('identify', user.id);
      }
      yield call(pingUserPolling);
    } else {
      yield put(userActions.failure(response.errors));
    }
  }
}

export function* getInfoVaAndQr(buyerName: string, buyerId: number) {
  const response = yield call(getVaAndQrInfoData, { buyerId: buyerId, buyerName: buyerName });
  if (!response?.errors) {
    yield put(setVaInfo(response));
  }
}

function* getRecommendationProductsForNewCustomer() {
  const params: QueryProductSearchArgs = {
    sort: [{ field: ProductSortField.Name, order: SortDirection.Asc }],
    filter: {
      isOnboardingBuyer: true,
      isRecommendation: true,
    },
    pagination: { page: 0, size: 100 },
  };
  const response = yield call(getProducts, params);
  if (!response.errors) {
    yield put(setRecommendationProductsForNewCustomer(response?.data));
  }
}

function* initBuyerFlow() {
  yield put(buyerActions.request());
  const response = yield call(getBuyer);
  const selectedStoreId = window.localStorage.getItem('store');

  if (!response.errors) {
    if (response.status === 'NOT_APPROVED') {
      yield put(signoutActions());
      message.error(translations(messages.waitingApprove));
    }
    if (response.status === 'DELETED' || response.status === 'DISABLED') {
      yield put(signoutActions());
      message.error(translations(messages.accountDeleted));
    } else {
      const stores = response.stores || [];
      const store = stores.find((s: any) => s.id === selectedStoreId);
      yield put(buyerActions.success(response));
      window.localStorage.setItem('region', response?.region);
      // if (!stores.length) {
      if (response.accountBuyerStatus === AccountBuyerStatus.NotActivated) {
        yield fork(getRecommendationProductsForNewCustomer);
      }
      if (!store) {
        window.localStorage.removeItem('store');
        if (stores.length || selectedStoreId) {
          yield put(openSelectStoreModal(true));
        }
      } else {
        window.localStorage.setItem('store', store.id);
        yield put(setSelectedStore(store));
        yield put(changeSelectedStore());
        // Call directly so history loads even if FETCH_SEARCH_HISTORY watcher
        // was not registered yet (e.g. guest boot → popup sign-in).
        yield call(getSearchHistory);
        const isFromReferral = sessionStorage.getItem('fromReferral');
        if (isFromReferral && isFromReferral === 'true') {
          sessionStorage.removeItem('fromReferral');
          yield put(push('/refer-friends'));
          return;
        }
      }
    }
  } else {
    yield put(signoutActions());
    yield put(buyerActions.failure(response.errors));
  }
}

function* signoutFlow() {
  while (true) {
    yield take(ActionTypes.SIGNOUT);
    yield call(unregisterToken);
    window.localStorage.removeItem('token');
    window.localStorage.removeItem('store');
    window.localStorage.removeItem('rememberToken');
    yield put(signoutSuccessActions());
    yield put(go('/' as any));
  }
}

let isPopupSigninProcessing = false;

function* popupSigninFlow() {
  while (true) {
    const {
      payload: { email, password, rememberMe, callback },
    } = yield take(ActionTypes.POPUP_SIGNIN_SUBMIT);

    if (isPopupSigninProcessing) {
      continue;
    }
    isPopupSigninProcessing = true;

    try {
      const response = yield call(signin, {
        email: email,
        password: password,
        rememberMe: rememberMe,
      });
      if (!response.errors) {
        localStorage.setItem('token', response.token);
        localStorage.setItem('rememberToken', response.rememberMeToken);
        localStorage.setItem('previousLoginEmail', email);
        localStorage.setItem('pendingGuestMerge', '1');
        message.success(translations(utilMessages.signinSuccess));
        yield put(setOpenSigninPopup(false));
        callback?.(true);
        yield put(openSelectStoreModal(true));
        // Refetch `me` here too — popup signin never restarts the saga bootstrap,
        // so without this user.permissions stays stale until a full page reload.
        yield all([call(initUserFlow), call(initBuyerFlow)]);
      } else {
        response.errors.map((e: any) => message.error(e.message));
        callback?.(false);
      }
    } finally {
      isPopupSigninProcessing = false;
    }
  }
}

function* favoriteFlow() {
  while (true) {
    const {
      payload: { product },
    } = yield take(ActionTypes.SET_FAVORITE);
    const id = product.id;
    yield put(favoriteActions.request());

    let response;
    if (product.isFavorite) {
      response = yield call(removeFavorite, { id: id });
    } else {
      response = yield call(addFavorite, { id: id });
    }

    if (response.errors) {
      yield put(favoriteActions.failure(response.errors));
    } else {
      yield put(favoriteActions.success({ id: id, favorite: !product.isFavorite, ...response }));
    }
  }
}

function* setProductNoteFlow() {
  while (true) {
    const { payload } = yield take(ActionTypes.SET_PRODUCT_NOTE);
    yield put(productNoteActions.request());
    const response = yield call(setProductNote, { input: payload });
    if (!response.errors) {
      yield put(productNoteActions.success(response.product));
      yield put(updateNoteToCard(response.product));
      showToast({
        message: translations(utilMessages[payload.note ? 'addItemNoteSuccessCheckout' : 'deleteItemNoteSuccess']),
      });
    } else {
      yield put(productNoteActions.failure(response.errors));
      message.error(translations(utilMessages[payload.note ? 'addItemNoteFailed' : 'deleteItemNoteFailed']));
    }
  }
}

function* updateCartFlow(action) {
  const {
    payload: { quantity, product },
  } = action;
  yield put(updateItemCartActions.request());

  const store = yield call(getStore);

  let response;
  if (quantity === 0) {
    response = yield call(removeCartItemLight, {
      input: { storeId: store?.id, supplierId: KAMEREO_SUPPLIER_ID, productId: product.id },
    });
  } else if (quantity) {
    yield put(updateProductItemQuantity({ productId: product.id, quantity: quantity }));
    const payload = { storeId: store?.id, supplierId: KAMEREO_SUPPLIER_ID, productId: product.id, quantity: quantity };
    yield put(addingToCartAction(payload));
    response = yield call(addCartItemLight, {
      input: payload,
    });
    yield put(addedToCartAction({ productId: product.id }));
    const quantityOfItemOnEachEvent = 1;
    triggerGA4EcommerceEvent(EventName.ADD_TO_CART, {
      ...product,
      quantity: quantityOfItemOnEachEvent,
    });
    if (window.location.pathname === '/tu-nong-trai-den-doanh-nghiep') {
      sendGA4Event(EventName.ADD_TO_CART_FROM_FARM_FTB, {
        product_category_ftb: `${product?.category?.parent?.name}/${product?.category?.name}`,
        product_sku_ftb: product?.supplierInternalCode,
        product_id_ftb: product?.id,
      });
    }
  }

  if (response.errors) {
    yield put(refreshCart());
    yield put(updateItemCartActions.failure(response.errors));
  } else {
    if (quantity === 0) {
      showToast({ message: translations(utilMessages.removeCartItemSuccess) });
    }
    yield put(updateItemCartActions.success());
  }
}

// SWR có ngưỡng cho categories (data ít đổi nhưng là navigation):
// - tuổi < REVALIDATE_AFTER: tin cache tuyệt đối, 0 network
// - REVALIDATE_AFTER..TTL:   paint từ cache ngay + revalidate nền
// - > TTL / đổi region / đổi version: miss cứng, fetch chặn
// Trần stale = 6h: Đổi category thì muộn nhất 6h sau user tự nhận bản mới.
const CATEGORIES_TTL_MS = 7 * 24 * 60 * 60 * 1000;
const CATEGORIES_REVALIDATE_AFTER_MS = 6 * 60 * 60 * 1000;
const CATEGORIES_CACHE_VERSION = 2;
const CATEGORIES_CACHE_KEY = 'categories';
const CATEGORIES_CACHE_ENABLED = true;

function categoriesCacheMeta() {
  return {
    v: CATEGORIES_CACHE_VERSION,
    region: window.localStorage.getItem('region') || '',
    lang: window.localStorage.getItem('lang') || 'vi',
  };
}

function refineCategories(rawCategories) {
  return rawCategories.map((category) => {
    if ('children' in category) {
      const childrenWithParent = category.children.map((child) => ({
        ...child,
        parent: category,
      }));
      return {
        ...category,
        children: childrenWithParent,
      };
    }
    return category;
  });
}

const toCategoryIds = (rawCategories) => rawCategories.map((category) => category.id);

function* fetchCategoriesAndCache(silentOnError = false) {
  const response = yield call(preloadCategoriesAndRevalidate);
  if (!response?.errors) {
    yield call(writeIdbCache, CATEGORIES_CACHE_KEY, response, categoriesCacheMeta());
    yield put(categoriesActions.success(refineCategories(response)));
    return toCategoryIds(response);
  }
  if (!silentOnError) {
    yield put(categoriesActions.failure(response.errors));
  }
  return null;
}

function* revalidateCategoriesFlow(cachedIds) {
  const freshIds = yield call(fetchCategoriesAndCache, true);
  if (freshIds && JSON.stringify(freshIds) !== JSON.stringify(cachedIds)) {
    yield call(initBrandByCategoryFlow, freshIds);
  }
}

function* initCategoriesFlow() {
  if (!CATEGORIES_CACHE_ENABLED) {
    return yield call(fetchCategoriesAndCache);
  }
  const entry = yield call(readIdbCacheEntry, CATEGORIES_CACHE_KEY, CATEGORIES_TTL_MS, categoriesCacheMeta());
  if (entry && Array.isArray(entry.data) && entry.data.length > 0) {
    yield put(categoriesActions.success(refineCategories(entry.data)));
    if (entry.ageMs > CATEGORIES_REVALIDATE_AFTER_MS) {
      yield fork(revalidateCategoriesFlow, toCategoryIds(entry.data));
    }
    return toCategoryIds(entry.data);
  }
  return yield call(fetchCategoriesAndCache);
}

function* getBrandsByCategoryId(categoryId: number) {
  const response = yield call(getBrandsRelatedToCategory, {
    filter: {
      categoryIds: [categoryId],
    },
    pagination: {
      page: 0,
      size: 6,
    },
  });

  if (response.errors) {
    yield put(
      brandsActions.success({
        data: [],
        categoryId: categoryId,
      }),
    );
  } else {
    yield put(
      brandsActions.success({
        data: response.data,
        categoryId: categoryId,
      }),
    );
  }
}

function* initBrandByCategoryFlow(categoryIds) {
  yield all(
    categoryIds.map((categoryId) => {
      return call(getBrandsByCategoryId, categoryId);
    }),
  );
}

// Same curated brand list as HomePage's "Top choice" widget (getBrandsData in HomePage/saga.ts),
// forked globally here so it's available on every page, not just while HomePage is mounted.
function* getTrustedBrandsFlow() {
  const topBrandsQA = [
    1098, 1096, 1092, 1093, 1097, 1094, 1095, 52, 51, 50, 49, 48, 47, 46, 45, 44, 43, 42, 41, 40, 39,
  ];
  const topBrandsPROD = [
    1208, 1176, 1153, 1154, 1178, 1175, 1177, 43, 158, 1, 101, 106, 31, 220, 10, 194, 144, 19, 36, 134, 40, 127, 72,
    246, 3, 45, 4,
  ];

  let brandIds = ['qa', 'development'].includes(process.env.APP_ENV) ? topBrandsQA : topBrandsPROD;
  const buyer = yield select(selectBuyer);
  const regionLocal = window.localStorage.getItem('region') || 'HCM';
  if (buyer.region === 'HN' || regionLocal === 'HN') {
    brandIds = brandIds.slice(6);
  }

  const brandResponse = yield call(getBrands, {
    brandIds: brandIds,
    pagination: { page: 0, size: brandIds.length },
  });

  if (brandResponse.errors) {
    yield put(trustedBrandsActions.failure(brandResponse.errors));
    return;
  }
  const arrangedBrands: any[] = [];
  brandIds.forEach((brandId) => {
    const brand = brandResponse?.data?.find((item) => Number(item.id) === brandId);
    if (brand) {
      arrangedBrands.push(brand);
    }
  });
  yield put(trustedBrandsActions.success(arrangedBrands));
}

function* initNavigationFlow() {
  const categoryIds = yield call(initCategoriesFlow);
  if (categoryIds) {
    yield call(initBrandByCategoryFlow, categoryIds);
  }
}

export function* initFavoriteCategoriesFlow() {
  yield put(favoriteCategoriesActions.request());
  const response = yield call(getCategories, {
    isFavorite: true,
  });
  if (!response.errors) {
    yield put(favoriteCategoriesActions.success(response));
  } else {
    yield put(favoriteCategoriesActions.failure(response.errors));
  }
}

function* routeChangeFlow() {
  while (true) {
    yield take('@@router/LOCATION_CHANGE');
    if (window.innerWidth < 1025) {
      yield put(sideNavClose());
    }
  }
}

export function* updateProductQuantity(action) {
  yield put(updateCart(action.payload));
}

export function* updateProductQuantityFlow() {
  while (true) {
    const action = yield take(ActionTypes.UPDATE_PRODUCT_QUANTITY);
    yield call(updateProductQuantity as any, action as any);
  }
}

function* viewPermissionsFlow() {
  while (true) {
    yield take('@@router/LOCATION_CHANGE');
    const userPermissions = yield select(selectUserPermissions());
    if (!userPermissions?.length) {
      continue;
    }
    const paths = Object.keys(viewPermissions);
    const pathname = yield select((state: any) => state.router.location.pathname);
    for (const path of paths) {
      const route = matchPath(pathname, { path: path });
      if (route) {
        const permissions = viewPermissions[route.path];
        if (permissions) {
          let permissionCounter = 0;
          for (const permission of userPermissions) {
            if (permissions.indexOf(permission) > -1) {
              permissionCounter += 1;
            }
          }
          if (permissionCounter !== permissions.length) {
            yield put(push('/'));
          }
        }
      }
    }
  }
}

// function* getNewWardForStore() {
//   const store = yield select(selectStore());
//   if (!store.id) {
//     return;
//   }

//   const response = yield call(searchNewWardForStoreAddress, {
//     storeUuid: store.id,
//   });
//   if (!response.errors) {
//     yield put(setRecommendNewWardForStore(response.data));
//   }
// }
// function* debounceSubmitProductQuantityFlow() {
//   yield put(submitMultipleProductQuantity());
// }

function* fetchOrderDisabled() {
  while (true) {
    yield take(ActionTypes.FETCH_ORDER_DISABLED_REQUEST);
    const response = yield call(getOrderDisabled);
    if (!response.errors) {
      yield put(orderDisabled.success(response));
    } else {
      yield put(orderDisabled.failure(response.errors));
    }
  }
}

let activeSearchInstances = 0;
let currentSearchTask: Task | null = null;
let currentlySearching = false;

export function* watchSearchProductResults() {
  activeSearchInstances += 1;

  if (activeSearchInstances > 1) {
    activeSearchInstances -= 1;
    return;
  }

  try {
    while (true) {
      const action = yield take(ActionTypes.SET_SEARCH_PRODUCT_RESULTS_REQUEST);

      if (currentSearchTask && currentSearchTask.isRunning()) {
        yield cancel(currentSearchTask);
      }

      currentSearchTask = yield fork(handleSearchProductResults, action);
    }
  } finally {
    activeSearchInstances -= 1;
  }
}

function* handleSearchProductResults(action: any) {
  try {
    if (currentlySearching) {
      return;
    }

    currentlySearching = true;
    const { query, clearPreviousData } = action.payload;
    yield put(setSearchProductResultsLoading(true));
    const searchProductResultsData = yield select(selectSearchProductResults());
    const search: QueryProductSearchArgs = {
      sort: [],
      filter: { query: query?.filter?.query },
      pagination: { page: 0, size: 20 },
    };
    const requestParams: QueryProductSearchArgs = { ...search };
    let requestPage = requestParams.pagination.page;

    if (searchProductResultsData.data.length < searchProductResultsData.totalResults) {
      const currentSearchProductResultsQuantity = searchProductResultsData.data.length;
      const productPerPage = requestParams.pagination.size;
      requestPage = Math.ceil(currentSearchProductResultsQuantity / productPerPage) + 1;
    }
    if (clearPreviousData) {
      yield put(setSearchProductResults.success({}));
      requestPage = 0;
    }
    // if (!search.sort.length) {
    //   requestParams.sort = [{ field: ProductSortField.Name, order: SortDirection.Asc }];
    // }
    requestParams.pagination = {
      ...requestParams.pagination,
      page: requestPage ? requestPage - 1 : 0,
    };

    const response = yield call(getProducts, {
      ...requestParams,
      sort: [],
    });
    if (!response.errors) {
      yield put(setSearchProductResults.success(response));
    } else {
      yield put(setSearchProductResults.failure(response.errors));
    }
  } catch (err) {
    yield put(setSearchProductResults.failure(err instanceof Error ? err : new Error(String(err))));
  } finally {
    currentlySearching = false;
    yield put(setSearchProductResultsLoading(false));
  }
}

export function* fetchBuyerWebVersion() {
  while (true) {
    yield take(ActionTypes.BUYER_WEB_VERSION);
    const url = `${process.env.APP_URL}/api/v1/buyerWebVersion`;
    const response = yield fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
    })
      .then((res) => res.json())
      .then((json) => json)
      .catch((error) => error);
    if (response.version) {
      yield put(setBuyerWebVersion(response));
    }
  }
}

function* refreshAfterCreateStore() {
  while (true) {
    yield take(ActionTypes.REFETCH_BUYER);
    if (!localStorage.getItem('token')) continue;
    yield call(initBuyerFlow);
    yield call(recentOrders);
  }
}

export function* getUoms() {
  const response = yield call(getAllUoms);

  if (!response.errors) {
    const dataJson = {};
    response.data.forEach((uom) => {
      dataJson[uom.code] = uom;
    });

    window.localStorage.setItem('uoms', JSON.stringify(dataJson));
  } else {
    window.localStorage.removeItem('uoms');
  }
}

const ORIGINS_TTL_MS = 7 * 24 * 60 * 60 * 1000;

export function* getAllOrigins() {
  if (readFreshCache<Record<string, unknown>>('origins', ORIGINS_TTL_MS, (d) => Object.keys(d).length > 0)) {
    return;
  }

  const response = yield call(getOrigins);

  if (!response.errors) {
    const dataJson = {};
    response.data.forEach((origin) => {
      dataJson[origin.name] = origin;
    });

    writeCache('origins', dataJson);
  } else if (!hasStaleCache('origins')) {
    invalidateCache('origins');
  }
}

export function* getDeliveryFee(action) {
  const { payload } = action;
  const store = yield call(getStore);
  if (!store || !payload?.totalPrice) {
    return;
  }

  const response = yield call(getDeliveryFeeQuery, {
    input: {
      storeId: store.id,
      totalPrice: payload.totalPrice,
    },
  });
  if (!response.errors) {
    yield put(
      getDeliveryFeeAction.success({
        isFreeDelivery: response.isFreeDelivery,
        fee: response.deliveryFee,
        remainingToFree: response.remainingToFree,
        deliveryFeeThreshold: response.deliveryFeeThreshold,
        additionalVat: response.additionalVat,
        areaGroupName: response.areaGroupName,
      }),
    );
  } else {
    yield put(getDeliveryFeeAction.failure(response.errors));
  }
}

function* handleClearMightFavorite() {
  while (true) {
    yield take(ActionTypes.SET_SELECTED_STORE);
    yield call(getPurchasedItemsMightFavorite, {
      type: 'MightFavoriteProductsWidget',
      widgetId: 'MIGHT_FAVORITE_PRODUCTS',
      item: {
        key: 'MIGHT_FAVORITE_PRODUCTS',
        title: '',
      },
    });
  }
}

export function* handleGetListHolidays() {
  const response = yield call(getListHolidays);
  const specialDates = new Map();
  response?.data?.forEach((item) => {
    specialDates.set(item.date, item);
  });
  yield put(setSpecialDates(specialDates));
}

export function* handleGetProductTags() {
  const response = yield call(getProductTagList);
  if (!response.errors) {
    yield put(setProductTags(response));
  }
}

export function* getBaseUoms() {
  const response = yield call(getAllBaseUoms);

  if (!response.errors) {
    const dataJson = {};
    response.data.forEach((uom: UomElement) => {
      dataJson[uom.id!] = uom;
    });
    window.localStorage.setItem('baseUoms', JSON.stringify(dataJson));
  } else {
    window.localStorage.removeItem('baseUoms');
  }
}

export function* getNetUoms() {
  const response = yield call(getAllNetUoms);

  if (!response.errors) {
    const dataJson = {};
    response.data.forEach((uom: UomElement) => {
      dataJson[uom.code!] = uom;
    });
    window.localStorage.setItem('netUoms', JSON.stringify(dataJson));
  } else {
    window.localStorage.removeItem('netUoms');
  }
}

export function* getSearchHistory() {
  const store = yield select(selectStore());
  if (!store?.internalId) {
    return;
  }
  const response = yield call(fetchSearchHistory, {
    storeId: store.internalId,
  });
  if (!response.errors) {
    yield put(setSearchHistory(response.data || []));
  }
}

function* handleSaveSearchHistory() {
  while (true) {
    const { payload } = yield take(ActionTypes.SAVE_SEARCH_HISTORY);
    const store = yield select(selectStore());
    if (!store?.internalId) {
      continue;
    }
    const searchHistory = yield select(selectSearchHistory());
    if (!(searchHistory || []).map((item) => item.keyword).includes(payload)) {
      const response = yield call(handleSaveSearchHistoryApi, {
        input: {
          storeId: store.internalId,
          keyword: payload,
        },
      });
      if (!response.errors && response.success) {
        yield fork(getSearchHistory);
      }
    }
  }
}

function* handleDeleteAllSearchHistory() {
  while (true) {
    yield take(ActionTypes.DELETE_SEARCH_HISTORY);
    const store = yield select(selectStore());
    if (!store?.internalId) {
      continue;
    }
    const response = yield call(handleDeleteSearchHistory, {
      input: {
        storeId: store.internalId,
        isDeleteAll: true,
      },
    });
    if (!response.errors && response.success) {
      yield put(setSearchHistory([]));
    }
  }
}

function* handleCheckBadWord(action) {
  const { payload } = action;
  const response = yield call(handleCheckBadWordApi, {
    word: payload.keyword,
  });
  if (!response.errors && response?.isValid && payload.cb) {
    payload.cb();
  }
}

function* handleGetTrendingSearchForGuestUser() {
  const response = yield call(fetchTrendingSearchForGuestUser);
  if (!response?.errors) {
    yield put(setTrendingSearchForGuestUser(response));
    yield put(
      setFooterState({
        trendingSearch: response?.trendingKeywords,
      }),
    );
  }
}

export function* handleGetRecommendSkusForSigninUser() {
  const response = yield call(fetchRecommendSkuForSigninUser);
  if (!response?.errors) {
    yield put(setRecommendSkusForSigninUser(response?.data));
  }
}

export function* handleUpdateStoreBasicInfo(action) {
  const {
    payload: { data, cb },
  } = action;

  const store = yield select(selectStore());
  if (!store?.id) {
    if (cb) cb(false);
    return;
  }

  const response = yield call(updateStoreBasicInfoApi, {
    input: {
      storeUuid: store.id,
      ...data,
    },
  });

  if (!response.errors) {
    message.success(translations(utilMessages.updateStoreInfoSuccess));
    if (cb) cb(true);
  } else {
    message.error(translations(utilMessages.updateStoreInfoFail));
    if (cb) cb(false);
  }
}

function* onChangeSelectedStore() {
  if (localStorage.getItem('pendingGuestMerge')) {
    yield call(checkAndMergeGuestCartApi, {
      input: {
        action: UpdateCartAction.MergeCarts,
      },
    });
    localStorage.removeItem('pendingGuestMerge');
  }
  // yield delay(100);
  yield put(refreshCart());
}

export function* handleGetFooterData() {
  yield fork(handleGetTrendingSearchForGuestUser);
  const topBrandsPROD = [43, 158, 1, 101, 106, 31, 220, 10, 194, 144, 19, 36, 134, 40, 127, 72, 246, 3, 45, 4];

  const brandIds = topBrandsPROD;
  const brandResponse = yield call(getBrands, {
    brandIds: brandIds,
    pagination: { page: 0, size: 20 },
  });

  if (!brandResponse.errors) {
    const arrangedBrands: any[] = [];
    brandIds.forEach((brandId) => {
      const brand = brandResponse?.data?.find((item) => Number(item.id) === brandId);
      if (brand) {
        arrangedBrands.push(brand);
      }
    });
    yield put(
      setFooterState({
        trustedBrands: arrangedBrands,
      }),
    );
  }
}

export default function* mainLayoutSaga() {
  const loggedIn = !!localStorage.getItem('token');

  yield fork(getUoms);
  yield fork(getAllOrigins);
  yield fork(initNavigationFlow);
  yield fork(getTrustedBrandsFlow);
  yield fork(routeChangeFlow);
  yield fork(initUserFlow);
  yield fork(watchSearchProductResults);
  yield fork(handleGetProductTags);
  yield fork(getBaseUoms);
  yield fork(getNetUoms);
  yield fork(handleGetFooterData);
  yield takeLatest(ActionTypes.CHECK_BAD_WORD, handleCheckBadWord);
  yield fork(popupSigninFlow);
  yield fork(signoutFlow);
  yield fork(refreshAfterCreateStore);

  // Enhance for guest cart
  yield takeEvery(ActionTypes.UPDATE_CART, updateCartFlow);
  yield fork(updateProductQuantityFlow);
  yield takeLatest(ActionTypes.REFRESH_CART, initCartFlow);
  yield takeLatest(ActionTypes.UPDATE_CART_SUCCESS, initCartFlow);
  yield takeLatest(ActionTypes.CHANGE_SELECTED_STORE, onChangeSelectedStore);
  // Register search-history watchers for guest boot too — popup sign-in never
  // restarts this saga, so otherwise FETCH/SAVE/DELETE actions are dropped.
  yield takeLatest(ActionTypes.FETCH_SEARCH_HISTORY, getSearchHistory);
  yield fork(handleSaveSearchHistory);
  yield fork(handleDeleteAllSearchHistory);
  // For guest users, fetch cart immediately. For logged-in users, skip this —
  // initBuyerFlow → changeSelectedStore → onChangeSelectedStore → merge → refreshCart
  // is the single source of truth, avoiding any race with a pre-merge fetch.
  if (!loggedIn) {
    yield put(refreshCart());
  }

  if (loggedIn) {
    yield fork(initBuyerFlow);
    yield fork(favoriteFlow);
    yield fork(viewPermissionsFlow);
    yield fork(recentOrders);
    yield fork(setProductNoteFlow);
    yield fork(registerToken);
    yield fork(markNotificationAsRead);
    yield fork(notificationsFlow);
    yield fork(fetchOrderDisabled);
    yield fork(fetchBuyerWebVersion);
    yield takeLatest(ActionTypes.GET_DELIVERY_FEE_REQUEST, getDeliveryFee);
    yield fork(handleClearMightFavorite);
    yield fork(handleGetListHolidays);
    yield fork(handleGetRecommendSkusForSigninUser);
    yield takeLatest(ActionTypes.UPDATE_STORE_BASIC_INFO, handleUpdateStoreBasicInfo);
    yield takeLatest(ActionTypes.OPEN_SELECT_STORE_MODAL, function* fetchAddStoreDisabled() {
      const response = yield call(getAddStoreDisabled);

      if (!response.errors) {
        yield put({ type: ActionTypes.FETCH_ADD_STORE_DISABLED_SUCCESS, payload: response.addStoreDisable });
      }
    });
  }
}
